import React, { useEffect, useState } from "react";
import {
  getDriverAllowanceStatement,
  getTripAllowanceSummary,
} from "../../api/apiGetrequest";
import AllowancePaymentModal from "./AddAllowancePayment";

const AllowanceSummaryPage = () => {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const [startDate, setStartDate] = useState(
    thirtyDaysAgo.toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);
  const [summaries, setSummaries] = useState([]);
  const [selectedStatement, setSelectedStatement] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchSummaries();
  }, [startDate, endDate]);

  const safeFormat = (value, places = 2) => {
    if (value === undefined || value === null || isNaN(value)) {
      return "0.00";
    }
    return Number(value).toFixed(places);
  };

  const fetchSummaries = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getTripAllowanceSummary(0, startDate, endDate);

      if (response && response.result) {
        const normalizedData = response.result.map((item) => ({
          userID: item.UserID || 0,
          fullName: item.FullName || "Unknown Driver",
          openingBalance: item.OpeningBalance || 0,
          tripAllowance: item.TripAllowance || 0,
          paidAmount: item.PaidAmount || 0,
          balance: item.Balance || 0,
        }));
        setSummaries(normalizedData);
      } else {
        setSummaries([]);
      }
    } catch (error) {
      console.error("Error fetching summaries:", error);
      setError("Failed to load data. Please try again.");
      setSummaries([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatement = async (userId) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getDriverAllowanceStatement(
        userId,
        startDate,
        endDate
      );

      if (response && response.result && Array.isArray(response.result)) {
        const normalizedStatements = response.result.map((item) => ({
          transactionDate: item.TransactionDate || new Date().toISOString(),
          description: item.Description || "No description",
          debit: item.Debit || 0,
          credit: item.Credit || 0,
          balance: item.Balance || 0,
        }));
        setSelectedStatement(normalizedStatements);
        setShowDialog(true);
      } else {
        setSelectedStatement([]);
        setError("No statement data available.");
        setShowDialog(true);
      }
    } catch (error) {
      console.error("Error fetching statement:", error);
      setError("Failed to load statement. Please try again.");
      setSelectedStatement([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setSelectedStatement([]);
  };

  return (
    <>
      {/* Scoped Styles */}
      <style>{`
        .allowance-summary * {
          box-sizing: border-box;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        
        .allowance-summary {
          padding: 25px;
          max-width: 1200px;
          margin: 0 auto;
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }
        
        .allowance-summary .page-title {
          font-size: 28px;
          color: #2c3e50;
          margin-bottom: 25px;
          border-bottom: 2px solid #eaeaea;
          padding-bottom: 10px;
        }
        
        .allowance-summary .date-controls {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 25px;
          background-color: #f5f7f9;
          padding: 15px;
          border-radius: 6px;
          align-items: center;
        }
        
        .allowance-summary .date-input-group {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-width: 200px;
        }
        
        .allowance-summary .date-label {
          font-weight: 600;
          margin-bottom: 8px;
          color: #445566;
        }
        
        .allowance-summary .date-input {
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .allowance-summary .date-input:focus {
          outline: none;
          border-color: #4a90e2;
          box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.25);
        }
        
        .allowance-summary .refresh-button {
          height: 40px;
          background-color: #4a90e2;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 0 15px;
          cursor: pointer;
          font-weight: 600;
          transition: background-color 0.2s;
          align-self: flex-end;
        }
        
        .allowance-summary .refresh-button:hover {
          background-color: #3a7bc8;
        }
        
        .allowance-summary .data-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .allowance-summary .data-table th {
          background-color: #445566;
          color: white;
          padding: 12px 15px;
          text-align: left;
          font-weight: 600;
        }
        
        .allowance-summary .data-table td {
          padding: 12px 15px;
          border-bottom: 1px solid #eaeaea;
          vertical-align: middle;
        }
        
        .allowance-summary .data-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        
        .allowance-summary .data-table tr:hover {
          background-color: #f0f4f8;
        }
        
        .allowance-summary .view-button {
          padding: 8px 12px;
          background-color: #4a90e2;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
          transition: background-color 0.2s;
        }
        
        .allowance-summary .view-button:hover {
          background-color: #3a7bc8;
        }
        
        .allowance-summary .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          animation: fadeIn 0.2s;
        }
        
        .allowance-summary .modal-content {
          background-color: white;
          padding: 25px;
          border-radius: 8px;
          width: 90%;
          max-width: 900px;
          max-height: 85vh;
          overflow-y: auto;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          animation: slideIn 0.3s;
        }
        
        .allowance-summary .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #eaeaea;
        }
        
        .allowance-summary .modal-title {
          margin: 0;
          font-size: 22px;
          color: #2c3e50;
        }
        
        .allowance-summary .close-button {
          padding: 8px 15px;
          background-color: #e74c3c;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.2s;
        }
        
        .allowance-summary .close-button:hover {
          background-color: #c0392b;
        }
        
        .allowance-summary .statement-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .allowance-summary .statement-table th {
          background-color: #445566;
          color: white;
          padding: 12px 15px;
          text-align: left;
        }
        
        .allowance-summary .statement-table td {
          padding: 12px 15px;
          border-bottom: 1px solid #eaeaea;
        }
        
        .allowance-summary .loading {
          display: flex;
          justify-content: center;
          padding: 20px;
          font-size: 16px;
          color: #666;
        }
        
        .allowance-summary .error {
          background-color: #ffebee;
          color: #e53935;
          padding: 10px 15px;
          border-radius: 4px;
          margin-bottom: 20px;
          border-left: 4px solid #e53935;
        }
        
        .allowance-summary .numeric-cell {
          text-align: right;
          font-family: monospace;
          font-size: 14px;
        }
        
        .allowance-summary .positive {
          color: #27ae60;
        }
        
        .allowance-summary .negative {
          color: #e74c3c;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .allowance-summary@media (max-width: 768px) {
          .allowance-summary .date-controls {
            flex-direction: column;
            align-items: stretch;
          }
          
          .allowance-summary .refresh-button {
            align-self: stretch;
          }
          
          .allowance-summary .data-table {
            display: block;
            overflow-x: auto;
            white-space: nowrap;
          }
        }
      `}</style>

      <div className="allowance-summary">
        <h1 className="page-title">Driver Allowance Summary</h1>

        <div className="date-controls">
          <div className="date-input-group">
            <label className="date-label">Start Date:</label>
            <input
              className="date-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="date-input-group">
            <label className="date-label">End Date:</label>
            <input
              className="date-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <button
            className="refresh-button"
            onClick={() => setIsModalOpen(true)}
          >
            Add Payment
          </button>
        </div>

        {error && <div className="error">{error}</div>}

        {isLoading ? (
          <div className="loading">Loading data...</div>
        ) : (
          <>
            {summaries && summaries.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Driver Name</th>
                    <th>Opening Balance</th>
                    <th>Trip Allowance</th>
                    <th>Paid Amount</th>
                    <th>Current Balance</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {summaries.map((summary) => (
                    <tr key={summary.userID || Math.random()}>
                      <td>{summary.fullName || "Unknown"}</td>
                      <td className="numeric-cell">
                        KES{safeFormat(summary.openingBalance)}
                      </td>
                      <td className="numeric-cell positive">
                        KES{safeFormat(summary.tripAllowance)}
                      </td>
                      <td className="numeric-cell negative">
                        KES{safeFormat(summary.paidAmount)}
                      </td>
                      <td
                        className={`numeric-cell ${
                          (summary.balance || 0) >= 0 ? "positive" : "negative"
                        }`}
                      >
                        KES{safeFormat(summary.balance)}
                      </td>
                      <td>
                        <button
                          className="view-button"
                          onClick={() => fetchStatement(summary.userID)}
                          disabled={!summary.userID}
                        >
                          View Statement
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "30px 0",
                  color: "#666",
                }}
              >
                No data available for the selected date range.
              </div>
            )}
          </>
        )}

        {showDialog && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Driver Allowance Statement</h2>
                <button className="close-button" onClick={handleCloseDialog}>
                  Close
                </button>
              </div>

              {selectedStatement && selectedStatement.length > 0 ? (
                <table className="statement-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStatement.map((statement, index) => {
                      let formattedDate = "N/A";
                      try {
                        if (statement.transactionDate) {
                          formattedDate = new Date(
                            statement.transactionDate
                          ).toLocaleDateString();
                        }
                      } catch (e) {
                        console.error("Date formatting error", e);
                      }

                      return (
                        <tr key={index}>
                          <td>{formattedDate}</td>
                          <td>{statement.description || "No description"}</td>
                          <td className="numeric-cell negative">
                            {(statement.debit || 0) > 0
                              ? `KES${safeFormat(statement.debit)}`
                              : "-"}
                          </td>
                          <td className="numeric-cell positive">
                            {(statement.credit || 0) > 0
                              ? `KES${safeFormat(statement.credit)}`
                              : "-"}
                          </td>
                          <td
                            className={`numeric-cell ${
                              (statement.balance || 0) >= 0
                                ? "positive"
                                : "negative"
                            }`}
                          >
                            KES{safeFormat(statement.balance)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px 0",
                    color: "#666",
                  }}
                >
                  No statement data available for this driver.
                </div>
              )}
            </div>
          </div>
        )}

        <AllowancePaymentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          users={summaries?.map((s) => ({
            id: s.userID,
            name: s.fullName,
          }))}
        />
      </div>
    </>
  );
};

export default AllowanceSummaryPage;